import { useEffect, useMemo, useState } from "react";
import TextInput from "../../component/textInput/TextInput";
import Invalid from "../../component/textInput/img/invalid.svg";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import Table from "../../component/table/Table";
import { useAuth } from "../../config/utilities/useAuth";
import { APIGetChilds } from "../../config/utilities/APIs/childApi";
import MapComponent from "../../component/map/MapComponent";
import "./Stops.css";
import Text from "../../component/text/Text";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import ConfirmStopModal from "../../component/modal/ConfirmStopModal";

const headers = [
  { key: "name", name: "Name", sortable: false },
  { key: "class", name: "Class", sortable: false },
  { key: "stopName", name: "Stops", sortable: false },
  { key: "tripSession", name: "Trip Session", sortable: false },
];

export const StopForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  input,
  setInput,
  error,
  setError,
  setLoading,
  initialInput,
  setInitialInput,
}) => {
  const { privilege, tripTypes } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [keyUp, setKeyUp] = useState("", false);
  const [showEmptyRow, setShowEmptyRow] = useState(false);
  const [originalStudentPool, setOriginalStudentPool] = useState([]);
  const [studentPool, setStudentPool] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [studentsWithStops, setStudentsWithStops] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fieldWidth = isMobileViewport ? "100%" : "45rem";
  const tableHeight = isMobileViewport ? "24rem" : "30rem";

  useEffect(() => {
    const fetchStops = async () => {
      setLoading(true);

      try {
        const response = await APIGetChilds(setLoading, institutionId);

        if (response.status === 200) {
          const rawStudents = response.data.data.childs;

          const formattedStudents = rawStudents.map((student) => ({
            id: student._id,
            name: `${student.firstName} ${student.lastName}`,
            class: student.class,
            stopData: student.stopData || null,
          }));
          setOriginalStudentPool(formattedStudents);
          setStudentPool(formattedStudents);
          setDataReady(true);
        }
      } catch (error) {
        console.error("Error fetching childs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStops();
  }, []);

  const isStopFormValid = () => {
    let isValid = true;

    const newErrors = {
      name: input.name === "" || input.name.length > 60,
      type: input.type === "" || input.type.length > 60,
      description: input.description === "" || input.description.length > 100,
      coordinates:
        !Array.isArray(input.location.coordinates) ||
        input.location.coordinates.length !== 2 ||
        input.location.coordinates.some((coord) => isNaN(coord)),
    };

    isValid =
      !newErrors.name &&
      !newErrors.type &&
      !newErrors.description &&
      !newErrors.coordinates;

    const isEdited = initialInput
      ? JSON.stringify(initialInput) !== JSON.stringify(input)
      : input.name.trim() !== "" ||
        input.type.trim() !== "" ||
        input.description.trim() !== "";

    return isValid && isEdited;
  };

  useEffect(() => {
    const noSpecialChars = /^[a-zA-Z0-9 ]*$/;

    const getFieldError = (
      value,
      fieldName,
      maxLength,
      skipSpecialCheck = false
    ) => {
      if (value.length > maxLength)
        return `${fieldName} should not exceed ${maxLength} characters`;
      if (!skipSpecialCheck && !noSpecialChars.test(value))
        return `${fieldName} contains invalid characters`;
      return "";
    };

    setError({
      name: getFieldError(input.name, "Name", 60, true),
      type: getFieldError(input.type, "Type", 60, true),
      description: getFieldError(input.description, "Description", 100, true),
      coordinates:
        !Array.isArray(input.location.coordinates) ||
        input.location.coordinates.length !== 2 ||
        input.location.coordinates.some((coord) => isNaN(coord)),
    });
  }, [input]);

  // A child may belong to stops with different trip sessions (e.g. Default + PM
  // + ECA). The only conflict is being assigned to the same session more than
  // once (e.g. two PM stops, or two Default stops).
  const sessionsOverlap = (a, b) => a === b;

  const getSessionLabel = (session) => {
    if (!session || session === "none") return "All";
    const match = tripTypes?.find((t) => t.id === session);
    return match?.name || session;
  };

  // A child cannot belong to two stops handling the same trip session. For each
  // associated child, compare this stop's selected session against the sessions
  // of every other stop the child already belongs to.
  const childConflicts = useMemo(() => {
    const currentStopId = existingData?._id || input?._id || "";

    return (input.associatedChilds || []).reduce((acc, child) => {
      const currentSession = child.tripSession || "none";

      const conflictingStops = (child.stopData || []).filter((stop) => {
        if (currentStopId && stop?.stopId === currentStopId) return false;
        return sessionsOverlap(currentSession, stop?.tripSession || "none");
      });

      if (conflictingStops.length > 0) {
        acc.push({
          id: child.id,
          name: child.name,
          currentSession,
          conflictingStops,
        });
      }

      return acc;
    }, []);
  }, [input.associatedChilds, existingData?._id, input._id]);

  const hasConflicts = childConflicts.length > 0;

  useEffect(() => {
    setIsSubmitDisabled(!isStopFormValid() || hasConflicts);
  }, [input, initialInput, hasConflicts]);

  const handleAddRowClick = () => {
    setShowEmptyRow(true);
  };

  const handleSaveRow = (newStudent) => {
    const studentWithSession = {
      ...newStudent,
      tripSession: "none",
    };

    setInput((prev) => ({
      ...prev,
      associatedChilds: [...prev.associatedChilds, studentWithSession],
    }));

    setStudentPool((prev) => prev.filter((s) => s.id !== newStudent.id));
    setShowEmptyRow(false);
  };

  const handleCancelRow = () => {
    setShowEmptyRow(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const handleMarkerPlaced = ({ longitude, latitude }, address) => {
    setInput((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        coordinates: [longitude, latitude],
      },
      address: address,
    }));
  };

  const handlePopoverAction = (action, rowId) => {
    if (action === "delete") {
      const removedStudent = input.associatedChilds.find(
        (student) => student.id === rowId
      );

      if (removedStudent) {
        setInput((prev) => ({
          ...prev,
          associatedChilds: prev.associatedChilds.filter(
            (student) => student.id !== rowId
          ),
        }));

        setStudentPool((prev) => [...prev, removedStudent]);
      }
    }
  };

  useEffect(() => {
    if (dataReady && (mode === "edit" || mode === "view") && existingData) {
      const associatedIds =
        existingData.associatedChilds?.map(
          (child) => child.childId || child._id
        ) || [];

      // Filter out already associated students
      setStudentPool(
        originalStudentPool.filter(
          (student) => !associatedIds.includes(student.id)
        )
      );

      const formattedAssociatedChilds = associatedIds.map((id) => {
        const fullDetails = originalStudentPool.find((s) => s.id === id) || {
          id,
          name: "Unknown Student",
          class: "Unknown",
          stopData: [],
        };

        // Find the stop data for THIS stop
        const stopInfo = fullDetails.stopData?.find(
          (s) => s.stopId === existingData._id
        );

        return {
          ...fullDetails,
          tripSession: stopInfo?.tripSession || "none",
        };
      });

      const formattedInput = {
        _id: existingData._id || "",
        name: existingData.name || "",
        description: existingData.description || "",
        address: existingData.address || "",
        location: existingData.location || { coordinates: [] },
        status: existingData.status || "",
        type: existingData.type || "",
        associatedChilds: formattedAssociatedChilds,
        updateScope: existingData.updateScope || "none",
      };

      setInitialInput(formattedInput);
      setInput(formattedInput);
    }
  }, [dataReady, mode, existingData, originalStudentPool]);

  const handleSubmit = () => {
    // Disable feature to check if student belongs to another stop and swap
    //const studentsWithStops = input.associatedChilds.filter(
    //  (child) => child.stopName && child.stopName !== (initialInput?.name ?? "")
    //);

    if (mode === "edit") {
      // setStudentsWithStops(studentsWithStops);
      setShowConfirmationModal(true);
    } else {
      onSubmit();
    }
  };

  const handleDropdownChange = (childId) => (e) => {
    const selectedSession = e.target.value;

    setInput((prev) => ({
      ...prev,
      associatedChilds: prev.associatedChilds.map((child) =>
        child.id === childId
          ? { ...child, tripSession: selectedSession }
          : child
      ),
    }));
  };

  if (loading) {
    return (
      <div className="content-loading-container">
        <LoadingSpinner
          className="loading-spinner"
          size="large"
          color="#ffb502"
        />
      </div>
    );
  }
  return (
    <>
      <div className="name-form-container">
        <div className="input-label">
          {<RequiredLabel>Stop Name</RequiredLabel>}
        </div>
        <div className="input-container">
          <TextInput
            placeholder={
              mode === "view" ? input.description : "Example: Lalaport BBCC"
            }
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="name"
            disable={mode === "view"}
            type="text"
            error={Boolean(error.name)}
            setError={setError}
            errorMessage={error.name}
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={fieldWidth}
            showCharCount
            maxLength={60}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Stop Type</RequiredLabel>
          <div className="input-description">
            Enter a type that best describes this stop.
          </div>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={
              mode === "view"
                ? input.description
                : "Example: Residence, Mall, School"
            }
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="type"
            disable={mode === "view"}
            type="text"
            error={Boolean(error.type)}
            setError={setError}
            errorMessage={error.type}
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={fieldWidth}
            showCharCount
            maxLength={60}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Stop Description</RequiredLabel>
          <div className="input-description">
            Add a brief description to add any additional details to the stop.
          </div>
        </div>
        <div className="input-container">
          <TextInput
            placeholder={
              mode === "view"
                ? input.description
                : "Example: Pickup at south entrance"
            }
            type="textarea"
            input={mode === "view" ? "" : input}
            setInput={setInput}
            name="description"
            width={fieldWidth}
            onChangeFunction={handleInputChange}
            error={Boolean(error.description)}
            setError={setError}
            errorMessage={error.description}
            keyUp={keyUp}
            setKeyUp={setKeyUp}
            disable={mode === "view"}
            showCharCount
            maxLength={100}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Location</RequiredLabel>
          <div className="input-description">
            Set the exact location of the stop using the search bar or by
            selecting a spot on the map. You can drag the marker to adjust its
            position after placing it.
          </div>
        </div>
        <div className="map-main-container">
          <div className="map-stop-container">
            <MapComponent
              onPlaceMarker={handleMarkerPlaced}
              showNavigation
              location={input.location}
              existing={existingData}
              mode={role === "VIEWER" ? "view" : "edit"}
            />
          </div>
          <div className="map-text-container">
            <Text
              text={
                input.location.coordinates.length
                  ? `Selected Coordinates: ${input.location.coordinates[0]}, ${input.location.coordinates[1]}`
                  : "Selected Coordinates: "
              }
              size="0.875rem"
              weight="500"
              color="#475467"
            />
            <Text
              text={`Stop Address: ${input?.address}`}
              size="0.875rem"
              weight="500"
              color="#475467"
            />
          </div>
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          Associated Students
          <div className="input-description">
            Add and remove students which are associated to this stop.
          </div>
        </div>
        <div
          className="input-container"
          style={{
            flex: 1,
            paddingBottom: "1rem",
            marginRight: isMobileViewport ? 0 : "1rem",
          }}
        >
          <Table
            data={input.associatedChilds || []}
            searchText={"Search by student name..."}
            headers={headers}
            showEmptyHeader={role !== "VIEWER"}
            showFilter={true}
            handlePopoverAction={handlePopoverAction}
            onClick={handleAddRowClick}
            module={"associatedStudent"}
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height={tableHeight}
            showEmptyRow={showEmptyRow}
            onSave={handleSaveRow}
            onCancel={handleCancelRow}
            optionData={studentPool}
            emptyStateSize="small"
            onRowChange={handleDropdownChange}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />

          {hasConflicts && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                marginTop: "0.75rem",
                wordWrap: "break-word",
                overflowWrap: "break-word",
                maxWidth: "100%",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <img
                  src={Invalid}
                  style={{
                    marginRight: "0.3rem",
                    flexShrink: 0,
                    marginTop: "0.125rem",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      color: "#f43f5e",
                      fontSize: "0.875rem",
                      fontFamily: "Inter",
                      lineHeight: "1.25rem",
                      wordWrap: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    Some students are assigned to a Trip Session that is already
                    handled by another stop. A student cannot belong to two
                    stops with the same Trip Session. Update the Trip Session to
                    resolve the conflict.
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginLeft: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.25rem",
                  maxHeight: "12rem",
                  overflowY: "auto",
                  paddingRight: "0.5rem",
                }}
              >
                {childConflicts.map((conflict) => (
                  <div
                    key={conflict.id}
                    style={{
                      color: "#f43f5e",
                      fontSize: "0.875rem",
                      lineHeight: "1.25rem",
                    }}
                  >
                    • {conflict.name}'s{" "}
                    <strong>{getSessionLabel(conflict.currentSession)}</strong>{" "}
                    session is already handled by{" "}
                    {conflict.conflictingStops
                      .map((stop) => stop?.stopName || "Unknown Stop")
                      .join(", ")}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="button-container-edit">
        <SecondaryButton
          width="6rem"
          label="Cancel"
          imageIcon={false}
          onClickFunction={handleReset}
        />
        {mode !== "view" && (
          <PrimaryButton
            width="6rem"
            disable={isSubmitDisabled}
            label={mode === "create" ? "Create" : "Save"}
            onClickFunction={handleSubmit}
            loading={loading}
          />
        )}
      </div>
      <ConfirmStopModal
        initialInput={initialInput}
        input={input}
        setInput={setInput}
        studentsWithStops={studentsWithStops}
        isOpen={showConfirmationModal}
        loading={loading}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={onSubmit}
      />
    </>
  );
};
